<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
// 오행분포 / 격국
import { VCol, VRow } from 'vuetify/lib/components/index.mjs';
import fiveMixin from '@/composables/fiveMixin';
import {
  splitFirstSentence,
  splitBySentence,
  splitArrayByPipe,
  transformMarkedText,
} from '@/composables/splitSentence';
import SajuEightTxt from './SajuEightTxt.vue';
import SajuFiveChart from '@/components/saju-insight/ohaeng/SajuFiveChart.vue';
import BasicContentsTitle from './BasicContentsTitle.vue';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useAnalysisStore } from '@/store/modules/analysis';

const analysisStore = useAnalysisStore();
const { transdata, birthData, birthJijanggan, curDaeun, sajuIlgan, sData } =
  storeToRefs(useAnalysisDataStore());

const userName = computed(() => analysisStore.userName || '');

const langMode = ref('CHNKOR');
const birthTitle = ref('시일월년');
// 일간에 active
const selectedData = ref(null);
const isActive = ref(false);

const { ...five } = fiveMixin();

const 오행분포 = computed(() => sData.value?.['오행분포']);
const 격국특성 = computed(() => sData.value?.['격국특성']);

// 오행
const 오행기본특성 = computed(() => splitFirstSentence(오행분포.value?.기본특성));
// const 오행핵심문장 = computed(() => 오행기본특성.value.first);
// const 오행핵심문장Html = computed(() => formatDoubleHash(오행핵심문장.value));
const 오행핵심문장Html = computed(() => `${userName.value}님의 중심에너지 입니다.`);
const 오행설명분리 = computed(() => splitArrayByPipe(오행기본특성.value.restSentences));
const 오행강점 = computed(() => 오행설명분리.value.before.map(transformMarkedText));
const 오행에너지 = computed(() => 오행설명분리.value.after.map(transformMarkedText));
const 오행상세특성 = computed(() =>
  splitBySentence(오행분포.value?.상세특성).map(transformMarkedText),
);
const 오행분포차트 = computed(() => 오행분포.value?.분포차트);
const 오행차트설명 = computed(() => 오행분포.value?.차트설명);

// 격국
const 격국기본특성 = computed(() => splitFirstSentence(격국특성.value?.기본특성));
const 격국핵심문장 = computed(() => transformMarkedText(격국기본특성.value.first));
const 격국설명 = computed(() => 격국기본특성.value.restSentences.map(transformMarkedText));
const 격국상세특성 = computed(() =>
  splitBySentence(격국특성.value?.상세특성).map(transformMarkedText),
);
const 격국사주위치 = computed(() => 격국특성.value?.사주위치);
const 격국풀이대상 = computed(() => 격국특성.value?.풀이대상);
</script>

<template>
  <VRow class="ss-full-height">
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card right">
        <BasicContentsTitle
          number="4"
          text="<span>나의 에너지는 어떤 모습&nbsp;</span><span>(오행으로 분석한 나의 에너지)</span>"
        />
        <section class="summ-body">
          <div style="width: 100%">
            <SajuFiveChart :fiveChardData="오행분포차트" :fiveChardDesc="오행차트설명" />
          </div>
          <div class="summ-body__result-analysis">
            <div
              class="key-sentence ganji-txt"
              :class="[five.getColorFive(sajuIlgan)]"
              v-html="오행핵심문장Html"
            />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">겉으로 드러나는 힘</div>
              <div class="result-analysis__desc" v-for="(line, index) in 오행강점" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">함께 작용하는 오행의 기운들</div>
              <div class="result-analysis__desc" v-for="(line, index) in 오행에너지" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">안에 깃든 에너지</div>
              <div class="result-analysis__desc" v-for="(line, index) in 오행상세특성" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
          </div>
          <div class="summ-body__result-img"></div>
        </section>
      </div>
    </VCol>
    <VCol cols="12" :md="halfCol" class="ss-full-height">
      <div class="summ-card">
        <BasicContentsTitle
          number="5"
          text="<span>관계속에서의 내 스타일&nbsp;</span><span>(격국으로 보는 나)</span>"
        />
        <section class="summ-body">
          <div class="summ-body__saju data-sec-01-box">
            <SajuEightTxt
              ref="ManseBirthMainArea"
              :pr-birth-data="birthData"
              :pr-birth-jijanggan="birthJijanggan"
              :pr-lang-mode="langMode"
              :pr-birth-title="birthTitle"
              :pr-transdata="transdata"
              :pr-type="'birth'"
              :pr-cur-daeun="curDaeun"
              :selected-data="selectedData"
              :is-active="isActive"
              :active-page="'page2'"
              :pr-position="격국사주위치"
              :pr-target="격국풀이대상"
            />
          </div>
          <div class="summ-body__result-analysis">
            <div
              class="key-sentence ganji-txt"
              :class="[five.getColorFive(sajuIlgan)]"
              v-html="격국핵심문장"
            />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">관계 속에서 만나는 나</div>
              <div class="result-analysis__desc" v-for="(line, index) in 격국설명" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
            <div class="summ-body__divider" />
            <div class="result-analysis__box">
              <div class="result-analysis__tit">나를 이끄는 기운의 틀</div>
              <div class="result-analysis__desc" v-for="(line, index) in 격국상세특성" :key="index">
                <span v-html="line"></span>
              </div>
            </div>
          </div>
          <div class="summ-body__result-img"></div>
        </section>
      </div>
    </VCol>
  </VRow>
</template>
